// One way to write a date across the app, "27 Oct 2026, 15:37": the month in words reads the same
// for an admin in any country, where 10.11.2026 does not
const DATE: Intl.DateTimeFormatOptions = { day: "numeric", month: "short", year: "numeric" };
const TIME: Intl.DateTimeFormatOptions = { hour: "2-digit", minute: "2-digit" };

const dateFormat = new Intl.DateTimeFormat("en-GB", DATE);
const dateTimeFormat = new Intl.DateTimeFormat("en-GB", { ...DATE, ...TIME });
const timeFormat = new Intl.DateTimeFormat("en-GB", TIME);
const dateTimeWithSecondsFormat = new Intl.DateTimeFormat("en-GB", {
  ...DATE,
  ...TIME,
  second: "2-digit",
});

// Intl throws a RangeError on an invalid date, which would take the whole screen down with it.
// en-GB spells every month in three letters but September, "Sept" in newer ICU data and "Sep" in
// older, so the month is cut to three: one width in a column of dates, the same in every runtime
const format = (formatter: Intl.DateTimeFormat, dateValue: Date | string) => {
  const date = typeof dateValue === "string" ? new Date(dateValue) : dateValue;
  if (Number.isNaN(date.getTime())) return "—";
  return formatter
    .formatToParts(date)
    .map((part) => (part.type === "month" ? part.value.slice(0, 3) : part.value))
    .join("");
};

/** "27 Oct 2026, 15:37"; with seconds where events a second apart must read apart (security logs). */
export const formatDateTime = (dateValue: Date | string, { seconds = false } = {}) =>
  format(seconds ? dateTimeWithSecondsFormat : dateTimeFormat, dateValue);

/** The time half of {@link formatDateTime}, "15:37", for a moment later today such as an expiry. */
export const formatTime = (dateValue: Date | string) => format(timeFormat, dateValue);

/** The date half of {@link formatDateTime}, so a date and a timestamp on one screen read alike. */
export const formatDate = (dateValue: Date | string) => format(dateFormat, dateValue);

/**
 * Today as "2026-10-30" for a file name: it sorts by date, and it is the admin's own calendar day, not
 * UTC's, so an evening export west of Greenwich is not dated tomorrow. Built from the date's parts, not a
 * locale, so no runtime's locale data can slip a "/" into the name.
 */
export const fileNameDate = () => {
  const today = new Date();
  const twoDigits = (value: number) => String(value).padStart(2, "0");
  return `${today.getFullYear()}-${twoDigits(today.getMonth() + 1)}-${twoDigits(today.getDate())}`;
};
